'use client';

import CartContext from '@/context/CartProvider';
import { useContext, useEffect, useState } from 'react';

import { sendGTMEvent } from '@/utils/gtm-event';
import { getLocalStorage, setLocalStorage } from '@/utils/local-storage';
import {
  EnumExperienceType,
  Experience,
  ExperienceCart,
  IDiscountCoupon,
  Order,
  Session,
  SessionCart,
  Ticket,
  TicketTypeCart,
  TicketTypeWithOptionalTicketTypes,
} from '@/utils/types';
import { useMemo } from 'react';

export const useCart = () => {
  const { cart, setCart, isLoadingCart, purchaseMode, switchToTicketsMode, crossSellPlan } = useContext(CartContext);
  const [cartStartTime, setCartStartTime] = useState<number | null>(null);

  const getCart = () => cart;

  const cartTicketsNumber = useMemo(() => {
    return cart.reduce((total: number, order: Order) => {
      return total + order.tickets.reduce((orderTotal, ticket) => orderTotal + ticket.amount, 0);
    }, 0);
  }, [cart]);

  const cartSubtotal = useMemo(() => {
    return cart.reduce((total: number, order: Order) => {
      return (
        total +
        order.tickets.reduce((orderTotal, ticket) => orderTotal + ticket.amount * (ticket.price + ticket.fee), 0)
      );
    }, 0);
  }, [cart]);

  const cartSubtotalNoFee = useMemo(() => {
    return cart.reduce((total: number, order: Order) => {
      return total + order.tickets.reduce((orderTotal, ticket) => orderTotal + ticket.amount * ticket.price, 0);
    }, 0);
  }, [cart]);

  const cartSubtotalFee = useMemo(() => {
    return cart.reduce((total: number, order: Order) => {
      return total + order.tickets.reduce((orderTotal, ticket) => orderTotal + ticket.amount * ticket.fee, 0);
    }, 0);
  }, [cart]);

  const createCartExperience = (experience: Experience): ExperienceCart => {
    return {
      id: experience.id,
      name: experience.name,
      image: experience.image,
      maxInstallments: experience.maxInstallments,
      installments: experience.installments,
      experiencesType: experience.experiencesType,
      minCanBePurchasedInExperience: experience.minCanBePurchasedInExperience,
      maxCanBePurchasedInDay: experience.maxCanBePurchasedInDay,
    };
  };

  const hasTicketInCart = (ticketId: string) => {
    return cart.some((order: Order) => order.tickets.some((ticket: Ticket) => ticket.id === ticketId));
  };

  const createCartSession = (session: Session): SessionCart => {
    return {
      startsAt: session.startsAt,
      endsAt: session.endsAt,
    };
  };

  const createCartTicketType = (ticket: TicketTypeWithOptionalTicketTypes): TicketTypeCart => {
    const ticketSource = ticket.TicketTypes || ticket;

    return {
      id: ticketSource.id,
      name: ticketSource.name,
      price: ticketSource.price?.cents === undefined ? 0 : ticketSource.price?.cents,
      fee: ticket.fee && ticket.fee?.cents !== undefined ? ticket.fee?.cents : 0,
    };
  };

  const createCartConfigurationId = (configurationId: string): string => {
    return configurationId;
  };

  const isAvailableTicketToAddCart = (experiencesTypes: EnumExperienceType[]) => {
    const isDifferentExperienceTypeInCart = cart.some(
      (order: any) => !experiencesTypes.includes(order?.experience?.experiencesType?.name)
    );
    return !isDifferentExperienceTypeInCart;
  };

  const addTicketToCart = (
    experience: ExperienceCart,
    session: SessionCart,
    ticket: TicketTypeCart,
    configurationId: string,
    amount: number,
    discountCoupon?: IDiscountCoupon[] | null,
    selectedSeats: string[] = [],
    selectedSeatsIds: string[] = []
  ) => {
    if (purchaseMode === 'plan') switchToTicketsMode?.();

    if (!isAvailableTicketToAddCart([experience?.experiencesType?.name] as EnumExperienceType[])) return;

    sendGTMEvent('add_to_cart', {
      ticket: {
        ...ticket,
        fee: ticket.fee === 0 ? 0 : ticket.fee / 100,
        price: ticket.price === 0 ? 0 : ticket.price / 100,
      },
      experience,
      session,
      selectedSeats,
      selectedSeatsIds,
    });

    const newCart = [...cart];
    const isStoredCartWithFee = getLocalStorage('cart_with_fee') && getLocalStorage('cart_with_fee')?.length;
    const cartWithFee = isStoredCartWithFee ? getLocalStorage('cart_with_fee') : (null as Order[] | null);

    const existingSession = newCart.find(
      (c: Order) =>
        c.experience.id === experience.id && c.startDate === session.startsAt && c.endDate === session.endsAt
    );

    if (existingSession) {
      const existingTicket = existingSession.tickets.find((t: Ticket) => t.id === ticket.id);
      existingSession.selectedSeats = selectedSeats;
      existingSession.selectedSeatsIds = selectedSeatsIds;

      if (existingTicket) {
        existingTicket.amount += amount;

        if (cartWithFee) {
          const selectedSession = cartWithFee.find(
            (c: Order) =>
              c.experience.id === experience.id && c.startDate === session.startsAt && c.endDate === session.endsAt
          );

          if (selectedSession) {
            const existingTicketInCartWithFee = selectedSession.tickets.find((t: Ticket) => t.id === ticket.id);

            if (existingTicketInCartWithFee) {
              const newCartWithFee = cartWithFee.map((order: Order) => {
                if (
                  order.experience.id === experience.id &&
                  order.startDate === session.startsAt &&
                  order.endDate === session.endsAt
                ) {
                  return {
                    ...order,
                    tickets: order.tickets.map((ticket) => ({
                      ...ticket,
                      amount: (existingTicketInCartWithFee.amount += amount),
                      fee: ticket.fee,
                    })),
                  };
                }
                return order;
              });
              setLocalStorage('cart_with_fee', newCartWithFee);
            }
          }
        }
      } else {
        existingSession.tickets.push({
          id: ticket.id,
          amount,
          price: ticket.price,
          name: ticket.name,
          fee: discountCoupon?.length ? 0 : ticket.fee,
        });

        if (cartWithFee && discountCoupon?.length) {
          const selectedSession = cartWithFee.find(
            (c: Order) =>
              c.experience.id === experience.id && c.startDate === session.startsAt && c.endDate === session.endsAt
          );

          if (selectedSession) {
            selectedSession.tickets.push({
              id: ticket.id,
              amount,
              price: ticket.price,
              name: ticket.name,
              fee: ticket.fee,
            });
            setLocalStorage('cart_with_fee', cartWithFee);
          }
        }
      }
    } else {
      const newOrder: Order = {
        experience: {
          id: experience.id,
          name: experience.name,
          image: experience.image,
          maxInstallments: experience.maxInstallments,
          installments: experience.installments,
          experiencesType: experience.experiencesType,
          minCanBePurchasedInExperience: experience.minCanBePurchasedInExperience,
          maxCanBePurchasedInDay: experience.maxCanBePurchasedInDay,
        },
        selectedSeats,
        selectedSeatsIds,
        startDate: session.startsAt,
        endDate: session.endsAt,
        tickets: [
          {
            id: ticket.id,
            amount,
            price: ticket.price,
            name: ticket.name,
            fee: discountCoupon?.length ? 0 : ticket.fee,
          },
        ],
        configurationId,
      };

      if (cartWithFee && discountCoupon?.length) {
        cartWithFee.push({
          ...newOrder,
          tickets: newOrder.tickets.map((ticket) => ({ ...ticket, fee: ticket.fee })),
        });
        setLocalStorage('cart_with_fee', cartWithFee);
      }

      newCart.push(newOrder);
    }

    setCart(newCart);

    return newCart;
  };

  const removeTicketFromCart = (
    experience: ExperienceCart,
    session: SessionCart,
    ticket: TicketTypeCart,
    amount: number
  ) => {
    sendGTMEvent('remove_from_cart', {
      ticket: {
        ...ticket,
        fee: ticket.fee === 0 ? 0 : ticket.fee / 100,
        price: ticket.price === 0 ? 0 : ticket.price / 100,
      },
      experience,
      session,
    });

    let newCart = [...cart];
    const isStoredCartWithFee = getLocalStorage('cart_with_fee') && getLocalStorage('cart_with_fee')?.length;
    const cartWithFee = isStoredCartWithFee ? getLocalStorage('cart_with_fee') : (null as Order[] | null);

    const existingSession = newCart.find(
      (c: Order) =>
        c.experience.id === experience.id && c.startDate === session.startsAt && c.endDate === session.endsAt
    );

    if (existingSession) {
      const existingTicket = existingSession.tickets.find((t: Ticket) => t.id === ticket.id);

      if (existingTicket) {
        const diff = existingTicket.amount - amount;
        existingTicket.amount -= diff;

        if (cartWithFee) {
          const selectedSession = cartWithFee.find(
            (c: Order) =>
              c.experience.id === experience.id && c.startDate === session.startsAt && c.endDate === session.endsAt
          );

          if (selectedSession) {
            let newCartWithFee = cartWithFee;
            const existingTicketInCartWithFee = selectedSession.tickets.find((t: Ticket) => t.id === ticket.id);

            if (existingTicketInCartWithFee) {
              newCartWithFee = cartWithFee.map((order: Order) => {
                if (
                  order.experience.id === experience.id &&
                  order.startDate === session.startsAt &&
                  order.endDate === session.endsAt
                ) {
                  return {
                    ...order,
                    tickets: order.tickets.map((ticket) => ({
                      ...ticket,
                      amount: (existingTicketInCartWithFee.amount -= diff),
                      fee: ticket.fee,
                    })),
                  };
                }
                return order;
              });

              if (existingTicketInCartWithFee.amount === 0)
                selectedSession.tickets = selectedSession.tickets.filter((t: Ticket) => t.id !== ticket.id);

              if (selectedSession.tickets.length === 0)
                newCartWithFee = newCartWithFee.filter(
                  (c: Order) =>
                    c.experience.id !== experience.id ||
                    c.startDate !== session.startsAt ||
                    c.endDate !== session.endsAt
                );

              setLocalStorage('cart_with_fee', newCartWithFee);
            }
          }
        }

        if (existingTicket.amount === 0) {
          existingSession.tickets = existingSession.tickets.filter((t: Ticket) => t.id !== ticket.id);
        }

        if (existingSession.tickets.length === 0) {
          newCart = newCart.filter(
            (c: Order) =>
              c.experience.id !== experience.id || c.startDate !== session.startsAt || c.endDate !== session.endsAt
          );
        }
      }
    }

    setCart(newCart);
  };

  const clearCart = () => {
    setCart([]);
    setLocalStorage('cart_with_fee', []);
    setCartStartTime(null);
  };

  const updateCartStartTime = () => {
    const currentTime = Date.now();
    setCartStartTime(currentTime);
    setLocalStorage('cart_start_time', currentTime);
  };

  const checkCartExpiration = (expirationTime: number) => {
    const storedStartTime = getLocalStorage('cart_start_time');
    if (storedStartTime) {
      const currentTime = Date.now();
      const timeDifference = currentTime - storedStartTime;

      if (timeDifference > expirationTime) {
        clearCart();
      }
    }
  };

  useEffect(() => {
    if (cart.length > 0) {
      updateCartStartTime();
    }
  }, [cart]);

  useEffect(() => {
    const expirationTime = 1800000; // 30 minutes
    const intervalId = setInterval(() => {
      checkCartExpiration(expirationTime);
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, []);

  return {
    cart,
    getCart,
    setCart,
    cartTicketsNumber,
    cartSubtotal,
    cartSubtotalNoFee,
    cartSubtotalFee,
    addTicketToCart,
    removeTicketFromCart,
    createCartExperience,
    createCartSession,
    createCartTicketType,
    createCartConfigurationId,
    isAvailableTicketToAddCart,
    hasTicketInCart,
    isLoadingCart,
    crossSellPlan,
  };
};

export default useCart;
